import { useState } from "react";
import { api, ApiError } from "../../api/client";
import type { WorkoutTemplate } from "../../api/types";
import { Badge, Button, EmptyState, ErrorState, Loading, Modal, PageHead, useToast } from "../../components/ui";
import { emptyWorkout, WorkoutForm, type WorkoutFormValue } from "../../components/WorkoutBuilder";
import { plannedDistance, plannedDuration, WORKOUT_TYPE_LABEL } from "../../lib/training";
import { useAsync } from "../../lib/useAsync";
import { t } from "../../i18n";

function toForm(tp: WorkoutTemplate): WorkoutFormValue {
  return {
    title: tp.name,
    workout_type: tp.workout_type,
    objective: tp.objective ?? "",
    instructions: tp.instructions ?? "",
    expected_rpe: tp.expected_rpe ? String(tp.expected_rpe) : "",
    structure: tp.structure,
    strength: tp.strength,
  };
}

export function TemplatesPage({ orgId }: { orgId: string }) {
  const toast = useToast();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<WorkoutTemplate | "new" | null>(null);
  const list = useAsync(
    () => api.get<WorkoutTemplate[]>(`/orgs/${orgId}/workout-templates`, { include_inactive: showArchived }),
    [orgId, showArchived],
  );

  async function toggle(tp: WorkoutTemplate) {
    try {
      await api.patch(`/orgs/${orgId}/workout-templates/${tp.id}`, { is_active: !tp.is_active });
      toast(tp.is_active ? t("Modelo “{name}” arquivado.", { name: tp.name }) : t("Modelo “{name}” reativado.", { name: tp.name }));
      list.reload();
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  return (
    <>
      <PageHead
        title={t("Modelos de treino")}
        subtitle={t("Treinos que você reutiliza. Aplicar um modelo copia o conteúdo para a planilha; editar o modelo depois não altera planilhas já montadas.")}
        actions={<Button variant="primary" onClick={() => setEditing("new")}>{t("Criar modelo")}</Button>}
      />
      <label className="checkbox" style={{ marginBottom: 12 }}>
        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
        {t("Mostrar arquivados")}
      </label>
      {list.loading && !list.data && <Loading />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {list.data && list.data.length === 0 && (
        <div className="panel">
          <EmptyState title={t("Nenhum modelo ainda")} action={<Button variant="primary" onClick={() => setEditing("new")}>{t("Criar modelo")}</Button>}>
            {t("Crie modelos como “Intervalado 6 × 800 m” ou “Longão de domingo” para montar planilhas mais rápido e prescrever para grupos.")}
          </EmptyState>
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="table-wrap">
          <table className="data responsive">
            <thead>
              <tr>
                <th>{t("Modelo")}</th>
                <th>{t("Tipo")}</th>
                <th className="right">{t("Distância")}</th>
                <th className="right">{t("Duração")}</th>
                <th className="right">{t("Ações")}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((tp) => (
                <tr key={tp.id}>
                  <td>
                    <strong>{tp.name}</strong> {!tp.is_active && <Badge>{t("Arquivado")}</Badge>}
                    {tp.totals.summary_text && <span className="sub">{tp.totals.summary_text}</span>}
                  </td>
                  <td data-label={t("Tipo")}>{WORKOUT_TYPE_LABEL[tp.workout_type]}</td>
                  <td data-label={t("Distância")} className="right num">
                    {plannedDistance(tp.totals.distance_m, tp.totals.distance_quality) || "—"}
                  </td>
                  <td data-label={t("Duração")} className="right num">
                    {plannedDuration(tp.totals.duration_s, tp.totals.duration_quality) || "—"}
                  </td>
                  <td className="right" style={{ whiteSpace: "nowrap" }}>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(tp)}>
                      {t("Editar")}
                    </Button>
                    <Button size="sm" variant={tp.is_active ? "danger" : "secondary"} onClick={() => toggle(tp)}>
                      {tp.is_active ? t("Arquivar") : t("Reativar")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={editing !== null} title={editing === "new" ? t("Novo modelo de treino") : t("Editar modelo")} onClose={() => setEditing(null)} wide>
        {editing !== null && (
          <WorkoutForm
            key={editing === "new" ? "new" : editing.id}
            orgId={orgId}
            mode="template"
            initial={editing === "new" ? emptyWorkout() : toForm(editing)}
            submitLabel={editing === "new" ? t("Criar modelo") : t("Salvar modelo")}
            onCancel={() => setEditing(null)}
            onSubmit={async (payload) => {
              const { title, ...rest } = payload;
              const body = { ...rest, name: title };
              if (editing === "new") await api.post(`/orgs/${orgId}/workout-templates`, body);
              else await api.patch(`/orgs/${orgId}/workout-templates/${editing.id}`, body);
              toast(editing === "new" ? t("Modelo criado.") : t("Modelo salvo."));
              setEditing(null);
              list.reload();
            }}
          />
        )}
      </Modal>
    </>
  );
}
